import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { forecastPaginatedTableMetricConfig } from 'src/features/forecastPaginated/forecastPaginatedTable/configs/forecastPaginatedTableMetricConfig';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

import {
  createTimeColumnCell,
  MetricCell,
  processMetricValue,
  type TableDataRow,
  TimeColumnHeader,
} from './forecastPaginatedTableUtils';

export type { TableDataRow };

export function useForecastPaginatedTableData(
  data: ForecastDataPoint[],
  sku: string,
  onUpdateForecastProposal: (
    sku: string,
    monthId: string,
    value: number,
  ) => void,
  editable: boolean,
): {
  tableData: TableDataRow[];
  timeColumns: string[];
  columns: ColumnDef<TableDataRow>[];
} {
  const { tableData, timeColumns } = useMemo(() => {
    if (!data || data.length === 0) return { tableData: [], timeColumns: [] };
    const timeColumns = data.map((point) => {
      const date = new Date(point.year, point.month - 1);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: '2-digit',
      });
    });
    const tableData: TableDataRow[] = forecastPaginatedTableMetricConfig
      .filter((metric) =>
        metric.optional
          ? data.some(
              (point) =>
                point[metric.key as keyof ForecastDataPoint] !== undefined,
            )
          : true,
      )
      .map((metric) => ({
        metric: metric.label,
        color: metric.color,
        values: data.map((point) =>
          processMetricValue(
            point[metric.key as keyof ForecastDataPoint],
            metric.key,
            point,
          ),
        ),
      }));
    return { tableData, timeColumns };
  }, [data]);

  const columns = useMemo((): ColumnDef<TableDataRow>[] => {
    const cols: ColumnDef<TableDataRow>[] = [
      {
        accessorKey: 'metric',
        header: '',
        cell: MetricCell,
        meta: {
          className:
            'sticky left-0 pl-2 z-10 h-14 w-44 border-r border-border-default bg-white dark:bg-neutral-900 text-layout-foreground',
        },
      },
    ];
    const cellRenderer = createTimeColumnCell(
      data,
      sku,
      onUpdateForecastProposal,
      editable,
    );
    timeColumns.forEach((timeColumn, index) => {
      cols.push({
        id: `time-${index}`,
        accessorFn: (row) => row.values[index],
        header: () => <TimeColumnHeader timeColumn={timeColumn} />,
        cell: cellRenderer,
        meta: {
          className:
            'relative size-14 border-r border-border-default text-center text-sm font-medium text-layout-foreground',
        },
      });
    });
    return cols;
  }, [timeColumns, data, sku, onUpdateForecastProposal, editable]);
  return { tableData, timeColumns, columns };
}
